import { createContext } from 'react';
import { useCallback } from 'react';
import { useContext } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createShellApi } from '@socialgoodsoftware/components';
import { switchOrganization } from '@socialgoodsoftware/components';
import { useOrganizationSwitching } from '@socialgoodsoftware/components';
import { useSessionStatus } from '@socialgoodsoftware/components';
import { OrganizationSelections } from '@socialgoodsoftware/libraries/services/organization-selection';
import { organizationApi } from '../organization-api';
import { userApi } from '../user-api';
import { useRunAs } from './RunAsContext';
import { featureQueryKey } from '../lib/api-client';
import { featureRoutes } from '../config/feature-routes';
import { buildLoginRedirectForCurrentLocation } from '../lib/login-redirect';
import { setOrganizationDeactivatedHandler } from '../lib/organization-deactivated-signal';
import { ClearOrganizationScopedState } from '../lib/organization-scoped-state';
import { completeAuthenticatedLanding } from '../lib/complete-authenticated-landing';

import type { ReactNode } from 'react';
import type { Organization } from '@socialgoodsoftware/components';
import type { OrganizationSelection } from '@socialgoodsoftware/components';
import type { OrganizationResponse } from '@contracts/account/organization';
import type { UserRecord } from '@contracts/account/user';

export type { OrganizationSelection };

type OrganizationContextValue = {
  user: UserRecord | null;
  organization: OrganizationResponse | null;
  organizationSelection: OrganizationSelection | null;
  switchingToOrganization: OrganizationSelection | null;
  organizations: Organization[];
  organizationsLoading: boolean;
  loadOrganizations: () => void;
  selectOrganization: (selection: OrganizationSelection) => void;
  switchOrganization: (selection: OrganizationSelection, continueUrl: string | null) => Promise<void>;
  recoverFromDeactivation: () => Promise<void>;
  refresh: () => void;
  isLoading: boolean;
};

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

const loginPath = '/login';

function routeRequiresSession(pathname: string): boolean {
  let requires = true;
  const match = featureRoutes.find((route) => route.path === pathname);
  if (match) {
    requires = match.archetype !== 'auth';
  }
  return requires;
}

async function listMemberships(): Promise<Organization[]> {
  let organizations: Organization[] = [];
  const response = await organizationApi.listForUser.query({});
  if (response.status === 200 && Array.isArray(response.body.organizations)) {
    organizations = response.body.organizations.map((entry) => ({ name: entry.name, precedence: entry.precedence }));
  }
  return organizations;
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { clearRunAs } = useRunAs();
  const deactivationInFlightRef = useRef(false);
  const requiresSession = routeRequiresSession(location.pathname);
  const shellApi = useMemo(() => createShellApi({ logoutUrl: loginPath }), []);

  const userQuery = userApi.getUser.useQuery({
    queryKey: featureQueryKey('user'),
    enabled: requiresSession,
    retry: false,
  });
  let userAuthenticated = false;
  if (userQuery.isSuccess && userQuery.data && userQuery.data.status === 200) {
    userAuthenticated = true;
  }
  const organizationQuery = organizationApi.getOrganization.useQuery({
    queryKey: featureQueryKey('organization', 'active'),
    queryData: { query: {} },
    enabled: requiresSession,
    retry: false,
  });

  const user = useMemo<UserRecord | null>(() => {
    let value: UserRecord | null = null;
    if (userQuery.isSuccess && userQuery.data && userQuery.data.status === 200) {
      value = userQuery.data.body;
    }
    return value;
  }, [userQuery.isSuccess, userQuery.data]);

  const organization = useMemo<OrganizationResponse | null>(() => {
    let value: OrganizationResponse | null = null;
    if (organizationQuery.isSuccess && organizationQuery.data && organizationQuery.data.status === 200) {
      value = organizationQuery.data.body;
    }
    return value;
  }, [organizationQuery.isSuccess, organizationQuery.data]);

  let userKey = '';
  if (user) {
    userKey = user.email;
  }

  const isLoading = organizationQuery.isLoading;

  const landAfterSwitch = useCallback(
    async (continueUrl: string | null): Promise<void> => {
      queryClient.clear();
      clearRunAs();
      ClearOrganizationScopedState();
      await completeAuthenticatedLanding({
        queryClient: queryClient,
        navigate: navigate,
        continueUrl: continueUrl,
      });
    },
    [queryClient, clearRunAs, navigate]
  );

  const landInPlace = useCallback(async (): Promise<void> => {
    await landAfterSwitch(null);
  }, [landAfterSwitch]);

  const switching = useOrganizationSwitching({
    logoutUrl: buildLoginRedirectForCurrentLocation(),
    userKey: userKey,
    getOrganizations: listMemberships,
    getSessionState: shellApi.getSessionState,
    landInPlace: landInPlace,
  });
  const selectedOrganization = switching.selectedOrganization;
  const commitSwitch = switching.commitSwitch;

  const refresh = useCallback(() => {
    void userQuery.refetch();
    void organizationQuery.refetch();
    switching.loadOrganizations();
  }, [userQuery, organizationQuery, switching]);

  const openOrganization = useCallback(
    async (selection: OrganizationSelection, continueUrl: string | null): Promise<void> => {
      await switchOrganization({ name: selection.name, precedence: selection.precedence });
      commitSwitch(selection);
      await landAfterSwitch(continueUrl);
    },
    [commitSwitch, landAfterSwitch]
  );

  const recoverFromDeactivation = useCallback(async (): Promise<void> => {
    if (deactivationInFlightRef.current) {
      return;
    }
    deactivationInFlightRef.current = true;
    try {
      let candidates: Organization[] = [];
      try {
        const memberships = await listMemberships();
        candidates = memberships.filter((entry) => !OrganizationSelections.IsSame(entry, selectedOrganization));
      } catch {
        candidates = [];
      }
      let switched = false;
      for (let index = 0; index < candidates.length && !switched; index = index + 1) {
        try {
          await openOrganization(candidates[index], null);
          switched = true;
        } catch {
          switched = false;
        }
      }
      if (!switched) {
        navigate('/logout', { replace: true });
      }
    } finally {
      deactivationInFlightRef.current = false;
    }
  }, [selectedOrganization, openOrganization, navigate]);

  useSessionStatus({
    redirectUrl: loginPath,
    resolveRedirectUrl: buildLoginRedirectForCurrentLocation,
    onOrgChange: switching.reactToOrganizationSelection,
    onOrgDeactivated: () => {
      void recoverFromDeactivation();
    },
    enabled: requiresSession && userAuthenticated && !isLoading && switching.switchingToOrganization === null,
  });

  useEffect(() => {
    setOrganizationDeactivatedHandler(() => {
      void recoverFromDeactivation();
    });
    return () => {
      setOrganizationDeactivatedHandler(null);
    };
  }, [recoverFromDeactivation]);

  const value: OrganizationContextValue = {
    user: user,
    organization: organization,
    organizationSelection: selectedOrganization,
    switchingToOrganization: switching.switchingToOrganization,
    organizations: switching.organizations,
    organizationsLoading: switching.organizationsLoading,
    loadOrganizations: switching.loadOrganizations,
    selectOrganization: switching.handleOrganizationChange,
    switchOrganization: openOrganization,
    recoverFromDeactivation: recoverFromDeactivation,
    refresh: refresh,
    isLoading: isLoading,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
